import {
    defineEntityConstellationDerivation,
    defineEntityItemDerivation,
    toEntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import type {
    EntityConstellationDerivation,
    EntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import {
    toPrimitiveWheelTitleCollectionKey,
    toPrimitiveWheelTitleConstellationKey,
    toPrimitiveWheelTitleItemKey,
} from '@/adapters/primitive/_pure/derivation/keyCodec';
import { toPrimitiveHubRingRadius, toPrimitiveWheelOpacity, toWheelLayout, toWheelPosition } from '@/adapters/primitive/_pure/derivation/wheels';
import type { Config, PrimitiveWheelRole, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';

const PRIMITIVE_WHEEL_TITLE_RENDER_ORDER = 100;

const PRIMITIVE_WHEEL_LABEL_BY_ROLE: Record<PrimitiveWheelRole, string> = {
    'addend-one': 'AddendOne',
    'addend-two': 'AddendTwo',
    sum: 'Sum',
    minuend: 'Minuend',
    subtrahend: 'Subtrahend',
    difference: 'Difference',
    multiplicand: 'Multiplicand',
    multiplier: 'Multiplier',
    'remaining-repetitions': 'Counter',
    product: 'Product',
    dividend: 'Dividend',
    divisor: 'Divisor',
    quotient: 'Quotient',
    remainder: 'Remainder',
    'identity-input': 'In',
    'identity-output': 'Out',
    'boolean-input-one': 'OperandOne',
    'boolean-input-two': 'OperandTwo',
    'boolean-output': 'Result',
    'less-than-source': 'LessThanSource',
    'less-than-target': 'LessThanTarget',
    'less-than-decision': 'LessThanDecision',
};

/** Result wheels keep `value: 0`; their displayed number lives in the counted-digit bands instead. */
function toPrimitiveWheelValue(wheel: PrimitiveWheelState): number | boolean | '?' {
    if (wheel.valueKind === 'boolean') return wheel.value;
    if (wheel.valueKind === 'comparison-helper') return '?';
    if (wheel.valueKind === 'placeholder') return '?';
    if (wheel.value !== 0) return wheel.value;
    return wheel.powers.reduce(
        (sum, power) => sum + (wheel.countedThroughDigitByPower?.[power] ?? 0) * 10 ** power,
        0,
    );
}

function toNormalizedTitleDirection(
    direction: readonly [number, number, number],
): [number, number, number] {
    const length = Math.hypot(...direction);
    if (length <= 1e-12) return [0, 1, 0];
    return direction.map((coordinate) => coordinate / length) as [number, number, number];
}

/** Helper wheels may span several render fragments, but own only one role flag. */
export function buildPrimitiveWheelTitleDerivations(
    config: Config,
    wheels: readonly PrimitiveWheelState[],
    titleDirection: readonly [number, number, number] = [0, 1, 0],
): {
    itemCollectionDerivations: EntityItemCollectionDerivation[];
    constellationDerivations: EntityConstellationDerivation[];
} {
    const itemCollectionDerivations: EntityItemCollectionDerivation[] = [];
    const constellationDerivations: EntityConstellationDerivation[] = [];

    if (!config.base.display.visibility.wheelTitleLabelsVisible) return { itemCollectionDerivations, constellationDerivations };
    const aboveItemStyle = config.base.display.label.aboveItemStyleOverride;
    const { wheelTitleFlagClearanceFontRatio, wheelTitleFlagMinTextPixels } = config.base.display.label;
    if (config.base.display.label.wheelTitlePresentationMode === 'flag'
        && (!Number.isFinite(wheelTitleFlagClearanceFontRatio) || wheelTitleFlagClearanceFontRatio < 0
            || !Number.isFinite(wheelTitleFlagMinTextPixels) || wheelTitleFlagMinTextPixels <= 0)) {
        throw new Error('Wheel title flags require a finite nonnegative clearance ratio and positive text pixel threshold.');
    }
    const normalizedTitleDirection = toNormalizedTitleDirection(titleDirection);
    const hasSecondBooleanOperand = wheels.some((wheel) => wheel.role === 'boolean-input-two');
    const titledHelpers = new Set<string>();

    for (const fragment of wheels) {
        const helper = fragment.helperWheel;
        if (fragment.variant !== undefined && helper === undefined) continue;
        if (helper && titledHelpers.has(helper.key)) continue;
        if (helper) titledHelpers.add(helper.key);
        const members = helper ? wheels.filter(wheel => wheel.helperWheel?.key === helper.key) : [fragment];
        const wheel = helper ? {
            ...fragment,
            powers: [...new Set(members.flatMap(member => member.powers))],
            wheelOpacity: Math.max(...members.map(toPrimitiveWheelOpacity)),
        } : fragment;
        const titleRef = { wheelRole: wheel.role, ...(helper ? { helperKey: helper.key } : {}) };
        const growingHelper = (helper?.kind === 'Assembly' || helper?.kind === 'Unit')
            && members.some(member => (member.entranceGrowth ?? 1) < 1);

        const itemKey = toPrimitiveWheelTitleItemKey(titleRef);
        const value = toPrimitiveWheelValue(wheel);
        const wheelLabel = helper?.kind ?? (wheel.role === 'boolean-input-one' && !hasSecondBooleanOperand
            ? 'Operand'
            : PRIMITIVE_WHEEL_LABEL_BY_ROLE[wheel.role]);
        const fontSize = aboveItemStyle?.fontSize ?? config.base.display.label.wheelTitleFontSize;
        const outerRadius = wheel.role === 'less-than-decision'
            ? config.base.display.wheel.innerRadius + config.base.display.wheel.ringWidth
            : wheel.valueKind === 'placeholder' || wheel.valueKind === 'comparison-helper'
                ? toPrimitiveHubRingRadius(config)
            : config.base.display.wheel.innerRadius
                + Math.max(0, ...wheel.powers) * (config.base.display.wheel.ringWidth + config.natural.display.wheel.ringGap)
                + config.base.display.wheel.ringWidth;
        const hasAngularLabels = (wheel.valueKind ?? 'natural') === 'natural'
            && (wheel.bandLabels?.angular ?? config.natural.display.label.angularBandLabelsVisible);
        // Give the incoming wheel a higher flag lane while keeping the post attached to its rim.
        const postLength = fontSize * (helper && helper.kind !== 'Assembly' ? 5 : 2.5);
        const postLean = hasAngularLabels ? fontSize * 4 : 0;
        const flag = config.base.display.label.wheelTitlePresentationMode === 'text' ? false : {
            postOffset: [-postLean, -postLength, 0] as [number, number, number],
            minTextPixels: wheelTitleFlagMinTextPixels,
        };
        const titleOffset = flag ? outerRadius + fontSize * wheelTitleFlagClearanceFontRatio
            : config.base.display.label.wheelTitleRadialOffset;
        const titlePosition = wheel.titlePosition ?? (wheel.role === 'less-than-decision'
            ? toWheelPosition({ layout: toWheelLayout({ config, operandSchedulingMode: config.natural.operandSchedulingMode }), role: wheel.role })
            : wheel.position);
        const anchorPosition: [number, number, number] = [
            titlePosition[0] + normalizedTitleDirection[0] * titleOffset + (flag ? postLean : 0),
            titlePosition[1] + normalizedTitleDirection[1] * titleOffset + (flag ? postLength : 0),
            titlePosition[2] + normalizedTitleDirection[2] * titleOffset,
        ];

        itemCollectionDerivations.push(
            toEntityItemCollectionDerivation({
                itemCollectionKey: toPrimitiveWheelTitleCollectionKey(titleRef),
                itemDerivations: [
                    defineEntityItemDerivation({
                        item: {
                            itemKind: 'entity',
                            itemKey,
                            name: `${wheel.role} title`,
                            description: `Label for the ${wheel.role} wheel.`,
                        },
                        parametricState: {
                            visualStyle: {
                                opacity: growingHelper ? 0
                                    : config.base.display.label.wheelTitleLabelOpacity * toPrimitiveWheelOpacity(wheel),
                            },
                            label: {
                                aboveItem: {
                                    ...(aboveItemStyle ?? {}),
                                    text: helper ? wheelLabel : wheel.roleless ? `${value}` : `${wheelLabel}: ${value}`,
                                    color: aboveItemStyle?.color ?? config.base.display.label.digitLabelColor,
                                    fontSize,
                                    flag,
                                    padding: aboveItemStyle?.padding ?? config.base.display.label.wheelTitlePadding,
                                    orientation: { orientationKind: 'billboard' },
                                    placement: { placementKind: 'item-origin' },
                                    backgroundColor: aboveItemStyle?.backgroundColor
                                        ?? config.base.display.label.backgroundColor,
                                    backgroundOpacity: aboveItemStyle?.backgroundOpacity
                                        ?? config.base.display.label.wheelTitleBackgroundOpacity,
                                    depthTest: false,
                                    renderOrder: PRIMITIVE_WHEEL_TITLE_RENDER_ORDER,
                                },
                            },
                        },
                    }),
                ],
            }),
        );

        constellationDerivations.push(
            defineEntityConstellationDerivation({
                constellation: {
                    constellationKind: 'entity',
                    constellationKey: toPrimitiveWheelTitleConstellationKey(titleRef),
                    itemCollectionKey: toPrimitiveWheelTitleCollectionKey(titleRef),
                    anchor: { itemKey, anchorKey: 'center' },
                    meta: { wheelRole: wheel.role },
                },
                parametricState: {
                    layout: {
                        // 'ring' only resolves an 'origin' anchor; 'sector-ring' honors an explicit world position.
                        layoutKind: 'sector-ring',
                        anchor: { anchorKind: 'position', position: anchorPosition },
                        innerRadius: 0,
                        outerRadius: 0.01,
                        angularGapFraction: 0,
                        arcSegments: 1,
                        rotation: [0, 0, 0],
                    },
                },
            }),
        );
    }

    return { itemCollectionDerivations, constellationDerivations };
}
